import { useEffect, useRef } from "react";

function isEditableElement(target) {
  if (!target) return false;
  const tagName = target.tagName;
  if (!tagName) return false;

  if (target.isContentEditable) return true;

  const normalizedTagName = tagName.toLowerCase();
  return (
    normalizedTagName === "input" ||
    normalizedTagName === "textarea" ||
    normalizedTagName === "select"
  );
}

function matchesBinding(event, binding) {
  const expectedKey = binding.key;
  if (expectedKey) {
    const eventKey = event.key ?? "";
    const keyMatches =
      expectedKey.length === 1
        ? eventKey.toLowerCase() === expectedKey.toLowerCase()
        : eventKey === expectedKey;
    if (!keyMatches) return false;
  }

  if (binding.mod && !(event.metaKey || event.ctrlKey)) return false;
  if (binding.ctrlKey !== undefined && event.ctrlKey !== binding.ctrlKey) return false;
  if (binding.metaKey !== undefined && event.metaKey !== binding.metaKey) return false;
  if (binding.shiftKey !== undefined && event.shiftKey !== binding.shiftKey) return false;
  if (binding.altKey !== undefined && event.altKey !== binding.altKey) return false;

  return true;
}

/**
 * Bind global keydown handlers with basic modifier support.
 *
 * @param {Array<{key?: string, mod?: boolean, ctrlKey?: boolean, metaKey?: boolean, shiftKey?: boolean, altKey?: boolean, allowInInput?: boolean, preventDefault?: boolean, handler: Function}>} bindings
 * @param {{enabled?: boolean}} options
 */
export function useGlobalHotkeys(bindings = [], options = {}) {
  const { enabled = true } = options;
  const bindingsRef = useRef(bindings);

  useEffect(() => {
    bindingsRef.current = bindings;
  }, [bindings]);

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event) => {
      const currentBindings = bindingsRef.current || [];
      if (currentBindings.length === 0) return;

      const isEditable = isEditableElement(event.target);

      for (const binding of currentBindings) {
        if (!binding) continue;
        if (isEditable && !binding.allowInInput) continue;
        if (!matchesBinding(event, binding)) continue;

        if (binding.preventDefault !== false) {
          event.preventDefault();
        }
        binding.handler(event);
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}

